import { useMemo } from 'react'
import type { ConstructorGrid, Pairing } from '../types'
import { getQualityReport, type QualityCheck } from '../lib/qualityChecks'

type ConstructorQualityPanelProps = {
  grid: ConstructorGrid | null
  pairings: Pairing[]
  onJumpToCell?: ((row: number, col: number) => void) | undefined
  onJumpToPlacement?: ((placementId: string) => void) | undefined
}

export function ConstructorQualityPanel({
  grid,
  pairings,
  onJumpToCell,
  onJumpToPlacement
}: ConstructorQualityPanelProps) {
  const report = useMemo(() => getQualityReport(grid, pairings), [grid, pairings])

  const jumpToIssue = (check: QualityCheck) => {
    if (!check.jumpTo) return
    onJumpToCell?.(check.jumpTo.row, check.jumpTo.col)
    if (check.jumpTo.placementId) onJumpToPlacement?.(check.jumpTo.placementId)
  }

  if (!grid) {
    return (
      <div className="w-80 rounded-xl border border-gray-200 bg-white shadow-sm p-3">
        <h3 className="text-sm font-semibold text-gray-900">Quality Checks</h3>
        <p className="mt-2 text-xs text-gray-500">Create a grid to run checks.</p>
      </div>
    )
  }

  return (
    <div className="w-80 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">Quality Checks</h3>
        <div className="mt-1 text-xs text-gray-500">
          {report.totalIssues} issue{report.totalIssues === 1 ? '' : 's'} · {report.errorCount} errors · {report.warningCount} warnings
        </div>
      </div>

      <div className="max-h-[420px] overflow-y-auto">
        {report.checks.length === 0 ? (
          <div className="p-3 text-xs text-emerald-700 bg-emerald-50 border-t border-emerald-100">
            No quality issues found in the current grid.
          </div>
        ) : (
          report.checks.map(check => {
            const severityClasses = check.severity === 'error'
              ? 'bg-red-50 text-red-700 border-red-100'
              : check.severity === 'warning'
                ? 'bg-amber-50 text-amber-700 border-amber-100'
                : 'bg-blue-50 text-blue-700 border-blue-100'

            return (
              <div key={check.id} className="px-3 py-2 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${severityClasses}`}>
                    {check.severity}
                  </span>
                  <span className="text-xs font-medium text-gray-900">{check.title}</span>
                  <span className="ml-auto text-xs font-semibold text-gray-700">{check.count}</span>
                </div>
                <p className="mt-1 text-xs text-gray-500">{check.description}</p>
                {check.details && check.details.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {check.details.map(detail => detail.jumpTo ? (
                      <button
                        key={detail.id}
                        onClick={() => jumpToIssue({ ...check, jumpTo: detail.jumpTo })}
                        className="text-[11px] px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100"
                      >
                        {detail.label}
                      </button>
                    ) : (
                      <span
                        key={detail.id}
                        className="text-[11px] px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-500"
                      >
                        {detail.label}
                      </span>
                    ))}
                  </div>
                )}
                {check.jumpTo && (
                  <button
                    onClick={() => jumpToIssue(check)}
                    className="mt-2 text-xs text-gray-600 hover:text-gray-900 underline underline-offset-2"
                  >
                    Jump to first
                  </button>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
